/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../authContext';
import { getOperatorKpis } from '../services/api';
import {
    formatCount,
    formatShare,
    paymentStatusTheme,
    PENDING_DEACTIVATION,
    TRACK,
} from '../utils/theme';
import { Swatch } from './Meters';
import { usesApio } from '../utils/navigation';
import styles from '../styles/Dashboard.module.css';

/*
 * Colores validados con validate_palette.js (guía dataviz) sobre la tarjeta
 * blanca: verde y rojo para altas y bajas; verde, ámbar y rojo en el anillo de
 * cobro. Lo que no tiene color propio (pausadas, canceladas) va como riel
 * vacío, igual que "Sin cobro" en el resumen.
 * Cada color va siempre con su nombre y su número al lado.
 */
const GREEN = paymentStatusTheme('apio', 'active').color;
const RED = paymentStatusTheme('apio', 'failed').color;
/** Más oscuro que el ámbar de Suspendida: el claro se pierde en barras grandes. */
const AMBER = '#e0a100';

const AGING_COLUMNS = [
    { key: 'upTo7', label: 'Menos de 7', color: AMBER },
    { key: 'upTo30', label: '7 a 30', color: AMBER },
    { key: 'upTo90', label: '30 a 90', color: AMBER },
    { key: 'over90', label: 'Más de 90', color: RED },
];

function monthDate(label) {
    const [year, month] = label.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, 15));
}

const monthName = (label) =>
    monthDate(label).toLocaleString('es-CL', { month: 'long', timeZone: 'UTC' });

const monthShort = (label) =>
    monthDate(label)
        .toLocaleString('es-CL', { month: 'short', timeZone: 'UTC' })
        .replace('.', '');

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const decimal = (value) =>
    value.toLocaleString('es-CL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function formatRate(rate) {
    if (rate === null || rate === undefined) return '—';
    return `${decimal(rate * 100)}%`;
}

function formatNet(value) {
    if (value > 0) return `+${formatCount(value)}`;
    if (value < 0) return `−${formatCount(-value)}`;
    return '0';
}

const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`;

/** Tope redondo para la escala, en medios órdenes de magnitud: 644 se dibuja contra 650. */
function niceMax(value) {
    if (value <= 0) return 10;
    const step = 10 ** Math.floor(Math.log10(value)) / 2;
    return Math.ceil(value / step) * step;
}

/** Barra vertical desde la base hasta el extremo, con solo el extremo redondeado. */
function barPath(x, width, base, end) {
    const height = Math.abs(end - base);
    if (height < 0.5) return '';
    const radius = Math.min(4, height, width / 2);
    const inward = end < base ? radius : -radius;
    return [
        `M${x},${base}`,
        `V${end + inward}`,
        `Q${x},${end} ${x + radius},${end}`,
        `H${x + width - radius}`,
        `Q${x + width},${end} ${x + width},${end + inward}`,
        `V${base}`,
        'Z',
    ].join(' ');
}

/** El número grande. Si hay un filtro equivalente en la tabla, es un botón que lo aplica. */
function Figure({ value, unit, filterStatus, currentStatus, onStatusChange }) {
    const content = (
        <>
            <span className={styles.kpiNumber}>{value}</span>
            <span className={styles.kpiUnit}>{unit}</span>
        </>
    );

    if (!filterStatus || !onStatusChange) {
        return <p className={styles.kpiFigure}>{content}</p>;
    }

    const pressed = currentStatus === filterStatus;
    return (
        <button
            type="button"
            className={`${styles.kpiFigure} ${styles.kpiFigureButton}`}
            aria-pressed={pressed}
            onClick={() => onStatusChange(pressed ? '' : filterStatus)}
        >
            {content}
            <span className={styles.kpiFilterHint}>
                {pressed ? 'Quitar el filtro de la tabla' : 'Ver en la tabla'}
            </span>
        </button>
    );
}

/* ---------- Movimiento ---------- */

function ChurnHighlight({ months }) {
    const previous = months[months.length - 2];
    const before = months[months.length - 3];
    if (!previous) return null;

    let delta = null;
    if (before && previous.churnRate !== null && before.churnRate !== null) {
        const points = (previous.churnRate - before.churnRate) * 100;
        const beforeName = monthName(before.label);
        delta =
            Math.abs(points) < 0.05
                ? `Igual que ${beforeName}`
                : `${decimal(Math.abs(points))} pts ${points > 0 ? 'más' : 'menos'} que ${beforeName}`;
    }

    return (
        <div className={styles.kpiHighlight}>
            <span className={styles.kpiHighlightLabel}>Churn de {monthName(previous.label)}</span>
            <span className={styles.kpiHighlightValue}>{formatRate(previous.churnRate)}</span>
            {delta && <span className={styles.kpiDelta}>{delta}</span>}
        </div>
    );
}

const monthTitle = (month, dayOfMonth) =>
    `${capitalize(monthName(month.label))}${month.partial ? `, al día ${dayOfMonth}` : ''}`;

const monthSummary = (month, dayOfMonth) =>
    `${monthTitle(month, dayOfMonth)}: ${plural(month.created, 'alta', 'altas')}, ${plural(
        month.terminated,
        'baja',
        'bajas'
    )}, neto ${formatNet(month.net)}, churn ${formatRate(month.churnRate)}`;

function TooltipRow({ color, label, value }) {
    return (
        <div className={styles.kpiTooltipRow}>
            <span>
                {color && <Swatch color={color} />}
                {label}
            </span>
            <span>{value}</span>
        </div>
    );
}

function ChartTooltip({ month, index, count, dayOfMonth }) {
    let position = { left: `${((index + 0.5) / count) * 100}%`, transform: 'translateX(-50%)' };
    if (index === 0) position = { left: 0 };
    if (index === count - 1) position = { right: 0 };

    return (
        <div className={styles.kpiTooltip} style={position} aria-hidden="true">
            <strong>{monthTitle(month, dayOfMonth)}</strong>
            <TooltipRow color={GREEN} label="Altas" value={formatCount(month.created)} />
            <TooltipRow color={RED} label="Bajas" value={formatCount(month.terminated)} />
            <TooltipRow label="Neto" value={formatNet(month.net)} />
            <TooltipRow label="Churn" value={formatRate(month.churnRate)} />
            <TooltipRow label="Activas al inicio" value={formatCount(month.activeAtStart)} />
        </div>
    );
}

const CHART_WIDTH = 600;
const CHART_HEIGHT = 170;
const CHART_PAD = 4;

/**
 * Altas hacia arriba y bajas hacia abajo, un mes por columna, sobre una misma
 * escala. El churn de cada mes va escrito bajo la columna: nunca un segundo eje.
 */
function MovementChart({ months, dayOfMonth }) {
    const [activeIndex, setActiveIndex] = useState(null);
    const baseline = CHART_HEIGHT / 2;
    const half = baseline - CHART_PAD;
    const max = niceMax(Math.max(...months.map((month) => Math.max(month.created, month.terminated))));
    const column = CHART_WIDTH / months.length;
    const barWidth = Math.min(34, column * 0.42);
    const length = (value) => (value / max) * (half - 1);
    const columns = { gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))` };

    return (
        <figure className={styles.kpiChart}>
            <div className={styles.kpiChartHead} aria-hidden="true">
                <ul className={styles.kpiLegendInline}>
                    <li>
                        <Swatch color={GREEN} />
                        Altas
                    </li>
                    <li>
                        <Swatch color={RED} />
                        Bajas
                    </li>
                </ul>
                <span className={styles.kpiChartScale}>Escala hasta {formatCount(max)}</span>
            </div>

            <div className={styles.kpiChartPlot}>
                <svg
                    className={styles.kpiChartSvg}
                    viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                    aria-hidden="true"
                    focusable="false"
                >
                    <line className={styles.kpiChartGrid} x1={0} x2={CHART_WIDTH} y1={CHART_PAD} y2={CHART_PAD} />
                    <line
                        className={styles.kpiChartGrid}
                        x1={0}
                        x2={CHART_WIDTH}
                        y1={CHART_HEIGHT - CHART_PAD}
                        y2={CHART_HEIGHT - CHART_PAD}
                    />
                    {months.map((month, index) => {
                        const x = column * index + (column - barWidth) / 2;
                        const dimmed = activeIndex !== null && activeIndex !== index;
                        return (
                            <g
                                key={month.label}
                                className={
                                    dimmed
                                        ? `${styles.kpiChartColumn} ${styles.kpiChartDimmed}`
                                        : styles.kpiChartColumn
                                }
                                fillOpacity={month.partial ? 0.5 : 1}
                            >
                                <path
                                    d={barPath(x, barWidth, baseline - 1, baseline - 1 - length(month.created))}
                                    fill={GREEN}
                                />
                                <path
                                    d={barPath(x, barWidth, baseline + 1, baseline + 1 + length(month.terminated))}
                                    fill={RED}
                                />
                            </g>
                        );
                    })}
                    <line className={styles.kpiChartBaseline} x1={0} x2={CHART_WIDTH} y1={baseline} y2={baseline} />
                </svg>

                <div className={styles.kpiChartHits} style={columns}>
                    {months.map((month, index) => (
                        <button
                            key={month.label}
                            type="button"
                            className={styles.kpiChartHit}
                            aria-label={monthSummary(month, dayOfMonth)}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseLeave={() => setActiveIndex(null)}
                            onFocus={() => setActiveIndex(index)}
                            onBlur={() => setActiveIndex(null)}
                        />
                    ))}
                </div>

                {activeIndex !== null && (
                    <ChartTooltip
                        month={months[activeIndex]}
                        index={activeIndex}
                        count={months.length}
                        dayOfMonth={dayOfMonth}
                    />
                )}
            </div>

            <div className={styles.kpiChartAxis} style={columns} aria-hidden="true">
                {months.map((month) => (
                    <div key={month.label} className={styles.kpiChartTick}>
                        <span className={styles.kpiChartMonth}>{monthShort(month.label)}</span>
                        <span className={styles.kpiChartChurn}>{formatRate(month.churnRate)}</span>
                    </div>
                ))}
            </div>
        </figure>
    );
}

function PendingDeactivation({ movement, status, onStatusChange }) {
    const count = plural(movement.pendingDeactivation, 'terminada', 'terminadas');
    const pressed = status === PENDING_DEACTIVATION;
    return (
        <p className={styles.kpiContext}>
            Más{' '}
            {onStatusChange ? (
                <button
                    type="button"
                    className={styles.kpiInlineFilter}
                    aria-pressed={pressed}
                    title={pressed ? 'Quitar el filtro de la tabla' : 'Ver en la tabla'}
                    onClick={() => onStatusChange(pressed ? '' : PENDING_DEACTIVATION)}
                >
                    {count} con baja programada
                </button>
            ) : (
                `${count} con baja programada`
            )}
            , que siguen con servicio hasta esa fecha:{' '}
            <strong>{formatCount(movement.active + movement.pendingDeactivation)} con servicio</strong>.
        </p>
    );
}

function MovementCard({ movement, status, onStatusChange }) {
    const { months, currentMonth } = movement;

    return (
        <article className={`${styles.tile} ${styles.kpiCard}`}>
            <h3 className={styles.kpiTitle}>Movimiento</h3>
            <div className={styles.kpiMovementTop}>
                <Figure
                    value={formatCount(movement.active)}
                    unit="suscripciones activas"
                    filterStatus="ACTIVATED"
                    currentStatus={status}
                    onStatusChange={onStatusChange}
                />
                <ChurnHighlight months={months} />
            </div>
            {movement.pendingDeactivation > 0 && (
                <PendingDeactivation
                    movement={movement}
                    status={status}
                    onStatusChange={onStatusChange}
                />
            )}
            <MovementChart months={months} dayOfMonth={currentMonth.dayOfMonth} />
        </article>
    );
}

/* ---------- Cobro ---------- */

const RING_SIZE = 148;
const RING_STROKE = 16;
const RING_GAP = 3;

/** Anillo partido por etapa de cobro, con el porcentaje al día al centro. */
function PaymentRing({ segments, total, value, unit, label }) {
    const center = RING_SIZE / 2;
    const radius = center - RING_STROKE / 2;
    const circumference = 2 * Math.PI * radius;
    const { arcs } = segments
        .filter((segment) => !segment.isTrack && segment.value > 0)
        .reduce(
            (acc, segment) => {
                const arcLength = (segment.value / total) * circumference;
                acc.arcs.push({
                    ...segment,
                    drawn: Math.max(arcLength - RING_GAP, Math.min(arcLength, 2)),
                    offset: acc.offset,
                });
                acc.offset += arcLength;
                return acc;
            },
            { arcs: [], offset: 0 }
        );

    return (
        <div className={styles.kpiRing}>
            <svg
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                role="img"
                aria-label={label}
            >
                <g transform={`rotate(-90 ${center} ${center})`}>
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke={TRACK}
                        strokeWidth={RING_STROKE}
                    />
                    {arcs.map((arc) => (
                        <circle
                            key={arc.key}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={arc.color}
                            strokeWidth={RING_STROKE}
                            strokeDasharray={`${arc.drawn} ${circumference}`}
                            strokeDashoffset={-arc.offset}
                        />
                    ))}
                </g>
            </svg>
            <div className={styles.kpiRingCenter} aria-hidden="true">
                <span className={styles.kpiRingValue}>{value}</span>
                <span className={styles.kpiRingUnit}>{unit}</span>
            </div>
        </div>
    );
}

/** Estados de Stripe en el anillo: los que no están aquí van juntos como riel vacío. */
const STRIPE_SEGMENTS = [
    { key: 'active', label: 'Al día', color: GREEN, statuses: ['active', 'trialing'] },
    { key: 'past_due', label: 'Pago atrasado', color: AMBER, statuses: ['past_due'] },
    { key: 'unpaid', label: 'Impaga', color: RED, statuses: ['unpaid'] },
];

function PaymentLegend({ label, segments }) {
    return (
        <ul className={styles.kpiLegend} aria-label={label}>
            {segments.map((segment) => (
                <li key={segment.key}>
                    <Swatch color={segment.color} isTrack={segment.isTrack} />
                    <span className={styles.kpiLegendLabel}>
                        {segment.label}
                        {segment.note && <span className={styles.kpiLegendNote}>{segment.note}</span>}
                    </span>
                    <span className={styles.kpiLegendValue}>{formatCount(segment.value)}</span>
                </li>
            ))}
        </ul>
    );
}

/** Para operadores que cobran solo por Stripe: el mismo anillo, por estado de la suscripción en Stripe. */
function StripePaymentsCard({ stripe }) {
    const byStatus = stripe.byStatus || {};
    const count = (statuses) => statuses.reduce((sum, key) => sum + (byStatus[key] || 0), 0);
    const known = STRIPE_SEGMENTS.flatMap((segment) => segment.statuses);
    const canceled = byStatus.canceled || 0;
    const rest = Object.entries(byStatus)
        .filter(([key]) => key !== 'canceled' && !known.includes(key))
        .reduce((sum, [, value]) => sum + value, 0);

    const segments = [
        ...STRIPE_SEGMENTS.map((segment) => ({ ...segment, value: count(segment.statuses) })),
        { key: 'canceled', label: 'Cancelada en Stripe', color: TRACK, isTrack: true, value: canceled },
        { key: 'rest', label: 'Otro estado', color: TRACK, isTrack: true, value: rest },
    ].filter((segment) => segment.key === 'active' || segment.value > 0);
    const upToDate = segments[0].value;

    const ringLabel = `Estado en Stripe: ${segments
        .map((segment) => `${segment.label} ${formatCount(segment.value)}`)
        .join(', ')}`;

    return (
        <article className={`${styles.tile} ${styles.kpiCard}`}>
            <h3 className={styles.kpiTitle}>Estado en Stripe</h3>
            {stripe.total > 0 ? (
                <div className={styles.kpiRingWrap}>
                    <PaymentRing
                        segments={segments}
                        total={stripe.total}
                        value={formatShare(upToDate, stripe.total)}
                        unit="al día"
                        label={ringLabel}
                    />
                    <PaymentLegend label="Estado en Stripe" segments={segments} />
                </div>
            ) : (
                <p className={styles.kpiContext}>No hay suscripciones vigentes cobradas por Stripe.</p>
            )}
        </article>
    );
}

function PaymentsCard({ payments }) {
    const { apio, stripe } = payments;
    const stage = (key) => apio.stages[key] || { total: 0, activated: 0, suspended: 0 };
    const upToDate = stage('active').total;
    const attempts = stage('attempt1').total + stage('attempt2plus').total;
    const attemptsActive = stage('attempt1').activated + stage('attempt2plus').activated;
    const failed = stage('failed');

    const segments = [
        { key: 'active', label: 'Al día', color: GREEN, value: upToDate },
        {
            key: 'attempts',
            label: 'En intentos fallidos',
            color: AMBER,
            value: attempts,
            note: attemptsActive > 0 ? plural(attemptsActive, 'sigue activa', 'siguen activas') : null,
        },
        {
            key: 'failed',
            label: 'Cobro fallido',
            color: RED,
            value: failed.total,
            note: failed.suspended > 0 ? plural(failed.suspended, 'suspendida', 'suspendidas') : null,
        },
        {
            key: 'rest',
            label: 'Pausadas u otro estado',
            color: TRACK,
            isTrack: true,
            value: stage('paused').total + stage('other').total,
        },
    ].filter((segment) => segment.key === 'active' || segment.value > 0);

    const ringLabel = `Estado en Apio: ${segments
        .map((segment) => `${segment.label} ${formatCount(segment.value)}`)
        .join(', ')}`;

    return (
        <article className={`${styles.tile} ${styles.kpiCard}`}>
            <h3 className={styles.kpiTitle}>Estado en Apio</h3>
            {apio.total > 0 ? (
                <div className={styles.kpiRingWrap}>
                    <PaymentRing
                        segments={segments}
                        total={apio.total}
                        value={formatShare(upToDate, apio.total)}
                        unit="al día"
                        label={ringLabel}
                    />
                    <PaymentLegend label="Estado en Apio" segments={segments} />
                </div>
            ) : (
                <p className={styles.kpiContext}>No hay suscripciones vigentes cobradas por Apio.</p>
            )}
            {stripe.total > 0 && (
                <p className={styles.kpiContext}>
                    En Stripe, {formatCount(stripe.active)} al día de {formatCount(stripe.total)}.
                </p>
            )}
        </article>
    );
}

/* ---------- Suspendidas ---------- */

function AgingColumns({ aging }) {
    const columns = AGING_COLUMNS.map((column) => ({ ...column, value: aging[column.key] || 0 }));
    const max = Math.max(...columns.map((column) => column.value), 1);

    return (
        <ul className={styles.kpiColumns} aria-label="Suspendidas por días desde el corte">
            {columns.map((column) => (
                <li key={column.key} className={styles.kpiColumn}>
                    <span className={styles.kpiColumnValue}>{formatCount(column.value)}</span>
                    <span className={styles.kpiColumnTrack} aria-hidden="true">
                        {column.value > 0 ? (
                            <span
                                className={styles.kpiColumnBar}
                                style={{
                                    height: `${(column.value / max) * 100}%`,
                                    background: column.color,
                                }}
                            />
                        ) : (
                            <span className={styles.kpiColumnEmpty} />
                        )}
                    </span>
                    <span className={styles.kpiColumnLabel}>{column.label}</span>
                </li>
            ))}
        </ul>
    );
}

function JobStatus({ retryJob }) {
    const { daysSinceLastAttempt: days, pendingCandidates, stale, intervalDays, batchSize, runsToClear } =
        retryJob;
    let last = 'No hay reintentos registrados';
    if (days === 0) last = 'Último reintento hoy';
    else if (days !== null) last = `Último reintento hace ${plural(days, 'día', 'días')}`;

    let pending = 'Ningún cobro espera reintento.';
    if (pendingCandidates > 0) {
        pending = `${plural(pendingCandidates, 'cobro espera', 'cobros esperan')} reintento`;
        pending +=
            runsToClear > 1
                ? `: a ${formatCount(batchSize)} por corrida, son ${plural(runsToClear, 'corrida', 'corridas')}.`
                : '.';
    }

    return (
        <p
            className={stale ? `${styles.kpiNotice} ${styles.kpiNoticeWarn}` : styles.kpiNotice}
            role={stale ? 'alert' : undefined}
        >
            <svg className={styles.kpiNoticeIcon} viewBox="0 0 16 16" aria-hidden="true">
                {stale ? (
                    <path
                        d="M8 1.8 15 14H1L8 1.8Zm0 4.2v4m0 2v.01"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                ) : (
                    <path
                        d="M13.5 8A5.5 5.5 0 1 1 8 2.5c1.6 0 3 .7 4 1.8M13.5 2.5v2.8h-2.8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                )}
            </svg>
            <span>
                <strong>Job de reintentos.</strong> {last}. {pending}
                {stale
                    ? ` Pasaron más de ${intervalDays} días sin reintentos: revisa que el job esté programado en Back4App.`
                    : ''}
            </span>
        </p>
    );
}

/**
 * Suspendidas por antigüedad del corte y las pasadas del job. `apio` apaga lo
 * que solo existe en operadores con Apio: el corte de SIM y el job de reintentos.
 */
function SuspendedCard({ suspended, retryJob, apio, status, onStatusChange }) {
    const withCutDate = Object.values(suspended.aging).reduce((sum, value) => sum + value, 0);

    if (suspended.total === 0) {
        return (
            <article className={`${styles.tile} ${styles.kpiCard}`}>
                <h3 className={styles.kpiTitle}>Suspendidas</h3>
                <Figure value="0" unit="con el servicio suspendido" />
                <p className={styles.kpiContext}>No hay suscripciones suspendidas.</p>
                {apio && <JobStatus retryJob={retryJob} />}
            </article>
        );
    }

    return (
        <article className={`${styles.tile} ${styles.kpiCard}`}>
            <h3 className={styles.kpiTitle}>Suspendidas</h3>
            <Figure
                value={formatCount(suspended.total)}
                unit="con el servicio suspendido"
                filterStatus="SUSPENDED"
                currentStatus={status}
                onStatusChange={onStatusChange}
            />

            {withCutDate > 0 && (
                <section className={styles.kpiSection}>
                    <h4 className={styles.kpiSectionTitle}>Días desde el corte del SIM</h4>
                    <AgingColumns aging={suspended.aging} />
                </section>
            )}

            <dl className={styles.kpiMiniStats}>
                <div>
                    <dt>Pagaron y siguen suspendidas</dt>
                    <dd>{formatCount(suspended.paymentRecovered)}</dd>
                </div>
                {apio && (
                    <div>
                        <dt>Sin corte de SIM confirmado</dt>
                        <dd>{formatCount(suspended.withoutCutDate)}</dd>
                    </div>
                )}
            </dl>

            {apio && <JobStatus retryJob={retryJob} />}
        </article>
    );
}

/* ---------- Contenedor ---------- */

function CardSkeleton({ visible }) {
    return visible ? <div className={`${styles.tile} ${styles.skeleton}`} aria-hidden="true" /> : null;
}

const formatGeneratedAt = (iso) =>
    new Date(iso).toLocaleString('es-CL', {
        timeZone: 'America/Santiago',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

/**
 * KPIs de un operador arriba de su lista: movimiento y churn de los últimos
 * meses, salud del cobro, y suspendidas con el estado del job de reintentos.
 */
export default function OperatorKpis({ project, operatorName, status, onStatusChange }) {
    const { tokens } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const latestRequest = useRef(0);
    // Sin Apio no hay embudo de Apio ni job de reintentos que mostrar.
    const apio = usesApio(operatorName);

    const load = useCallback(
        async (refresh = false) => {
            if (!tokens?.AccessToken) return;
            const requestId = latestRequest.current + 1;
            latestRequest.current = requestId;
            setLoading(true);
            setError(null);
            try {
                const result = await getOperatorKpis(tokens.AccessToken, project, operatorName, {
                    refresh,
                });
                if (latestRequest.current === requestId) setData(result);
            } catch (requestError) {
                if (latestRequest.current !== requestId) return;
                setError(
                    requestError.response?.data?.message ||
                        'No se pudieron cargar los indicadores. La lista de abajo sigue disponible.'
                );
            } finally {
                if (latestRequest.current === requestId) setLoading(false);
            }
        },
        [tokens?.AccessToken, project, operatorName]
    );

    useEffect(() => {
        load(false);
    }, [load]);

    let meta = '';
    if (loading) meta = data ? 'Actualizando' : 'Calculando indicadores';
    else if (data) meta = `Actualizado ${formatGeneratedAt(data.generatedAt)}${data.cached ? ' (cache)' : ''}`;

    return (
        <section className={styles.kpis} aria-label={`Indicadores de ${operatorName}`}>
            <header className={styles.kpiHead}>
                <h2 className={styles.kpiHeading}>Indicadores</h2>
                <div className={styles.kpiHeadActions}>
                    <p className={styles.meta} aria-live="polite">
                        {meta}
                    </p>
                    <button
                        type="button"
                        className={styles.kpiRefresh}
                        disabled={loading}
                        onClick={() => load(true)}
                    >
                        Actualizar
                    </button>
                </div>
            </header>

            {error && <p className={`${styles.tile} ${styles.kpiError}`}>{error}</p>}

            <div className={styles.kpiGrid}>
                {data ? (
                    <MovementCard
                        movement={data.movement}
                        status={status}
                        onStatusChange={onStatusChange}
                    />
                ) : (
                    <CardSkeleton visible={loading} />
                )}
                {data ? (
                    apio ? (
                        <PaymentsCard payments={data.payments} />
                    ) : (
                        <StripePaymentsCard stripe={data.payments.stripe} />
                    )
                ) : (
                    <CardSkeleton visible={loading} />
                )}
                {data ? (
                    <SuspendedCard
                        suspended={data.suspended}
                        retryJob={data.retryJob}
                        apio={apio}
                        status={status}
                        onStatusChange={onStatusChange}
                    />
                ) : (
                    <CardSkeleton visible={loading} />
                )}
            </div>
        </section>
    );
}
